import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Loader2, Send, RefreshCw, Bug, Lightbulb, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// iter132 Phase C — Admin support ticket dashboard. Table of all
// tickets with filters, expand-row to see full description +
// change status + post admin reply. On status/reply change the user
// gets a Resend email (best-effort, backend-side).

const TYPE_META = {
  bug:      { icon: Bug,        color: "var(--bt-loss)" },
  idea:     { icon: Lightbulb,  color: "var(--bt-accent)" },
  question: { icon: HelpCircle, color: "var(--bt-text-muted)" },
};

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (type) params.set("type", type);
      if (emailFilter.trim()) params.set("user_email", emailFilter.trim());
      const { data } = await api.get(`/admin/support/tickets?${params.toString()}`);
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status, type]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-support-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors" data-testid="admin-support-back">
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </Link>

      <header>
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Admin</p>
        </div>
        <h1 className="font-heading text-3xl">Support Tickets</h1>
      </header>

      <div className="grid grid-cols-4 gap-3" data-testid="admin-support-counts">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(status === s ? "" : s)}
            className={`border rounded-sm p-3 text-left transition-colors ${
              status === s ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10" : "border-[var(--bt-border)] bg-[var(--bt-surface)] hover:border-[var(--bt-text-muted)]"
            }`}
            data-testid={`admin-support-count-${s}`}
          >
            <p className="bt-label">{STATUS_LABEL[s]}</p>
            <p className="font-heading text-2xl">{counts[s] ?? 0}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-3">
        <span className="bt-label">TYPE</span>
        <button onClick={() => setType("")} className={`px-3 py-1 border rounded-sm text-sm ${!type ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10" : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"}`} data-testid="admin-support-type-all">All</button>
        {Object.keys(TYPE_META).map((t) => (
          <button key={t} onClick={() => setType(type === t ? "" : t)} className={`px-3 py-1 border rounded-sm text-sm capitalize ${type === t ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10" : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"}`} data-testid={`admin-support-type-${t}`}>{t}</button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <Input
            type="email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") refresh(); }}
            placeholder="Filter by user email…"
            className="w-56"
            data-testid="admin-support-email-filter"
          />
          <Button variant="outline" size="sm" onClick={refresh} data-testid="admin-support-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--bt-text-muted)]">Loading…</p>
      ) : tickets.length === 0 ? (
        <div className="border border-dashed border-[var(--bt-border)] rounded-sm p-8 text-center" data-testid="admin-support-empty">
          <p className="text-sm text-[var(--bt-text-muted)]">No tickets match these filters.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-support-list">
          {tickets.map((t) => (
            <TicketRow
              key={t.id}
              t={t}
              expanded={openId === t.id}
              onToggle={() => setOpenId(openId === t.id ? null : t.id)}
              onUpdated={() => refresh()}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketRow({ t, expanded, onToggle, onUpdated }) {
  const TypeIcon = TYPE_META[t.type]?.icon || HelpCircle;
  const typeColor = TYPE_META[t.type]?.color || "var(--bt-text-muted)";
  const [status, setStatus] = useState(t.status);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(t.status); setReply(""); }, [t.id, t.status]);

  const dirty = status !== t.status || reply.trim().length > 0;

  const submit = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      const body = {};
      if (status !== t.status) body.status = status;
      if (reply.trim()) body.admin_reply = reply.trim();
      await api.patch(`/admin/support/tickets/${t.id}`, body);
      toast.success("Ticket updated — user notified by email");
      setReply("");
      onUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm" data-testid={`admin-support-row-${t.id}`}>
      <button onClick={onToggle} className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-[var(--bt-surface-hover)] transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <TypeIcon className="w-4 h-4 flex-none" style={{ color: typeColor }} />
          <div className="min-w-0">
            <p className="font-medium truncate">{t.title}</p>
            <p className="text-xs text-[var(--bt-text-muted)] truncate">
              {t.user_email} · {new Date(t.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
            </p>
          </div>
        </div>
        <span className="text-xs px-2 py-0.5 border border-[var(--bt-border)] rounded-sm shrink-0 capitalize">
          {STATUS_LABEL[t.status]}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-[var(--bt-border)] p-4 space-y-4">
          <div>
            <p className="bt-label mb-1">Description</p>
            <p className="text-sm whitespace-pre-wrap">{t.description}</p>
          </div>
          {t.admin_reply && (
            <div>
              <p className="bt-label mb-1">Previous reply ({t.admin_reply_by_email})</p>
              <p className="text-sm whitespace-pre-wrap border-l-2 border-[var(--bt-accent)] pl-3 py-1">{t.admin_reply}</p>
            </div>
          )}
          <div>
            <p className="bt-label mb-1.5">Status</p>
            <div className="flex flex-wrap gap-2" data-testid={`admin-support-status-group-${t.id}`}>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1 border rounded-sm text-sm capitalize ${
                    status === s ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]" : "border-[var(--bt-border)] text-[var(--bt-text-muted)]"
                  }`}
                  data-testid={`admin-support-status-${t.id}-${s}`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="bt-label mb-1.5">Reply to user (optional — user will get an email)</p>
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Short note back to the user…"
              rows={3}
              maxLength={5000}
              data-testid={`admin-support-reply-${t.id}`}
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={!dirty || busy}
              data-testid={`admin-support-save-${t.id}`}
              className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Send className="w-4 h-4 mr-1.5" /> Save & notify</>}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
